import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { browserHistory, withRouter } from 'react-router'
import { toString } from 'lodash'
import { TrackingHOC } from 'hocs'
import CallHandler from 'components/CallHandler'
import {
  selectSessionId,
  selectStartTime,
  selectIsStarted,
  selectCallDuration,
  selectAppId
} from './selectors'
import { startCall, stopCall, getSession } from './actions'
import { selectFaq } from '../Faq/selectors'
import { OptionsHOC } from '../../hocs'
import { postWindowMessage } from 'helpers/utils'
import { selectUser } from '../App/selectors'
import { createFootprints } from '../Footprints/actions'
import { useKendra } from '../../hooks'
import * as KEYS from '../../constants/keys'
import { postMessageToBotWindow, sendCallSessionToBot } from '../../helpers/bot'

const defaultCallData = {
  showFeedback: false,
  secondsElapsed: 0,
  firstFaq: null,
  lastFaq: null
}

const CallHandlerContainer = ({
  faq,
  startCall,
  startFaq,
  createEvent,
  startSessionText,
  endSessionText,
  clientIdInputEnabled,
  startTime,
  inputValidationErrorMessage,
  inputRegexp,
  sessionId,
  stopCall,
  isStarted,
  inputInfo,
  inputPlaceholder,
  feedbackButtons,
  getSession,
  router,
  user,
  bypassFeedback,
  createFootprints,
  trackFootprints,
  appId
}) => {
  const [data, setData] = useState(defaultCallData)
  const [validInput, setValidInput] = useState(null)
  const [clientId, setClientId] = useState('')
  const [validationMessage, setValidationMessage] = useState('')
  const { auth } = router.routes[0]
  const { setSearch } = useKendra()

  const handleFeedback = (resolved) => {
    const { firstFaq, lastFaq, secondsElapsed } = data

    if (!bypassFeedback && !validInput) return

    createEvent({
      category: 'callData',
      data: {
        action: 'StopCall',
        resolved: toString(resolved),
        duration: secondsElapsed,
        call_session_id: sessionId,
        client_id: clientId,
        first_article_name: firstFaq ? firstFaq.name : null,
        first_article_pk: firstFaq ? firstFaq.PK : null,
        last_article_name: lastFaq ? lastFaq.name : null,
        last_article_pk: lastFaq ? lastFaq.PK : null
      }
    })
    stopCall({
      userID: user.user_id
    })
    setData(defaultCallData)
    if (trackFootprints) {
      createFootprints(clientId)
      sessionStorage.setItem(KEYS.APP_INSTANCE_ID, Math.uuid())
    }
    postWindowMessage('devicebits:stopCall')
    browserHistory.push('/')
  }

  const handleStart = () => {
    const uuid = Math.uuid()
    const validStartFaq =
      startFaq && typeof startFaq === 'string' && /^[0-9]{7}$/.test(startFaq)
    const startingRoute = validStartFaq ? `/faq/${startFaq}` : '/'
    setClientId('')
    setValidInput(null)
    startCall({
      engagementID: uuid,
      appID: sessionStorage.getItem(KEYS.APP_INSTANCE_ID),
      userID: user.user_id
    })

    sessionStorage.setItem(KEYS.CALL_SESSION_ID, uuid)

    // Push the new id immediately rather than waiting for START_CALL.SUCCESS, so
    // the bot is attributed correctly even if the create-session API is slow. A
    // later BOT_READY re-send covers a widget that reloads as part of the restart.
    sendCallSessionToBot(uuid)

    createEvent({
      category: 'callData',
      data: {
        action: 'StartCall',
        call_session_id: uuid
      }
    })

    postWindowMessage('devicebits:resetBot')

    return browserHistory.push(startingRoute)
  }

  const handleFinish = () => {
    // Clear the call session on end so post-call analytics carry no stale id;
    // the next call's handleStart pushes a fresh one. Windowed bots are cleared
    // by the confirmLogout below (the widget wipes its sessionStorage).
    sessionStorage.removeItem(KEYS.CALL_SESSION_ID)
    sendCallSessionToBot('');
    window.botInstance?.sendMessage({ type: 'RESET_CHAT' });
    window.botInstance?.close();
    if (window.botWindowReference) {
      postMessageToBotWindow( { event: 'confirmLogout' }, '*')
    }
    setData({
      ...data,
      showFeedback: !bypassFeedback,
      secondsElapsed: (new Date().getTime() - startTime) / 1000
    })
    if (bypassFeedback) {
      return handleFeedback(true)
    }
  }

  const hideFeedback = () => {
    setData({
      ...data,
      showFeedback: false
    })
  }

  const inputValidation = (value) => {
    // Regexp not present, validation not needed
    if (!inputRegexp) return
    const r = new RegExp(inputRegexp)
    // Value doesn't satisfy regex, set validation errors
    if (!value.match(r)) {
      setValidInput('error')
      setValidationMessage(inputValidationErrorMessage || 'Invalid')
      return
    }

    // If it reached this step the regexp matched and we can clear errors
    setValidationMessage('')
    setValidInput('success')
  }

  const handleChange = (e) => {
    setClientId(e.target.value)
    inputValidation(e.target.value)
  }

  const logout = () => {
    // Clear the search state on auth.logout no matter what
    setSearch({ type: 'IS_KENDRA_ENABLED', payload: false });
    setSearch({ type: 'INDEX_ID', payload: null });
    auth.logout()
  }

  const generateProps = () => ({
    ...data,
    // TODO why aren't these props provided? @date 2024-12-03
    // ...props,
    // faq,
    // startCall,
    // startFaq,
    // createEvent,
    // startTime,
    // inputValidationErrorMessage,
    // inputRegexp,
    // sessionId,
    // stopCall,
    // getSession,
    // router,
    // user,
    // bypassFeedback,
    // createFootprints,
    // trackFootprints,
    appId,
    isStarted,
    handleStart,
    handleChange,
    handleFeedback,
    handleFinish,
    logout,
    inputInfo,
    clientId,
    startSessionText,
    endSessionText,
    clientIdInputEnabled,
    validationMessage,
    inputPlaceholder,
    feedbackButtons,
    validInput,
    hideFeedback
  })
  useEffect(() => {
    getSession(user.user_id)
  }, [])

  // getSession restores an in-progress call asynchronously, and the bot can
  // become ready before that resolves — so BOT_READY on its own would leave a
  // resumed call logging a null id. Push whenever the active session id changes.
  // Only a truthy id is pushed: until the restore resolves we do not yet know
  // that there is no call, so we must not clear an id the bot already holds.
  // Ending a call clears it explicitly in handleFinish.
  useEffect(() => {
    if (sessionId) {
      sendCallSessionToBot(sessionId)
    }
  }, [sessionId])

  useEffect(() => {
    if (!data.firstFaq && faq.PK) {
      return setData({
        ...data,
        firstFaq: faq,
        lastFaq: faq
      })
    }
    return setData({
      ...data,
      lastFaq: faq.PK ? faq : null
    })
  }, [faq])

  useEffect(() => {
    if (appId !== null) {
      sessionStorage.setItem(KEYS.APP_INSTANCE_ID, appId)
    }
  }, [appId])

  return <CallHandler {...generateProps()} />
}

const mapStateToProps = (state, props) => ({
  sessionId: selectSessionId(state),
  isStarted: selectIsStarted(state),
  startTime: selectStartTime(state),
  callDuration: selectCallDuration(state),
  faq: selectFaq(state),
  startSessionText: props.getOption('callHandler.startSessionText'),
  endSessionText: props.getOption('callHandler.endSessionText'),
  clientIdInputEnabled: props.getOption('callHandler.clientIdInput'),
  inputInfo: props.getOption('callHandler.inputInfo'),
  inputRegexp: props.getOption('callHandler.inputRegexp'),
  inputPlaceholder: props.getOption('callHandler.inputPlaceholder'),
  feedbackButtons: props.getOption('callHandler.feedback'),
  inputValidationErrorMessage: props.getOption(
    'callHandler.inputValidationErrorMessage'
  ),
  startFaq: props.getOption('callHandler.startFaq'),
  user: selectUser(state),
  trackFootprints: props.getOption('trackFootprints'),
  bypassFeedback: props.getOption('callHandler.bypassFeedback'),
  appId: selectAppId(state)
})

CallHandlerContainer.propTypes = {
  faq: PropTypes.shape({
    PK: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    val: PropTypes.string.isRequired
  }).isRequired,
  startCall: PropTypes.func.isRequired,
  startFaq: PropTypes.string,
  createEvent: PropTypes.func.isRequired,
  clientIdInputEnabled: PropTypes.bool,
  startTime: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  inputValidationErrorMessage: PropTypes.string,
  inputRegexp: PropTypes.string,
  sessionId: PropTypes.string,
  stopCall: PropTypes.func.isRequired,
  isStarted: PropTypes.bool.isRequired,
  inputInfo: PropTypes.string,
  inputPlaceholder: PropTypes.string,
  feedbackButtons: PropTypes.shape({
    disabledButtonColor: PropTypes.string,
    leftButtonColor: PropTypes.string,
    leftButtonText: PropTypes.string,
    optional1ButtonColor: PropTypes.string,
    optional1ButtonText: PropTypes.string,
    optional2ButtonColor: PropTypes.string,
    optional2ButtonText: PropTypes.string,
    rightButtonColor: PropTypes.string,
    rightButtonText: PropTypes.string
  }),
  getSession: PropTypes.func.isRequired,
  user: PropTypes.shape({
    user_id: PropTypes.string.isRequired
  }).isRequired,
  createFootprints: PropTypes.func.isRequired,
  trackFootprints: PropTypes.bool,
  appId: PropTypes.string
}

const mapDispatchToProps = (dispatch) => ({
  startCall: (data) => dispatch(startCall.request(data)),
  stopCall: (data) => dispatch(stopCall.request(data)),
  getSession: (userId) => dispatch(getSession.request(userId)),
  createFootprints: (clientId) => dispatch(createFootprints.request(clientId))
})

export default TrackingHOC(
  OptionsHOC(
    connect(
      mapStateToProps,
      mapDispatchToProps
    )(withRouter(CallHandlerContainer))
  )
)
